// ════════════════════════════════════════════════════════════════════
// TERROR — X4's FACE · THE TOURNAMENT BOARD
// ────────────────────────────────────────────────────────────────────
//   <TournamentBoard uid={uid} name={displayName} />
//
// A lobby, a bracket, and a standings column. The bracket is rendered
// straight from deriveBracket's rounds array — no recursion, no tree
// walk, no per-node component. That is the payoff of the immutable-
// rounds decision in tournament.js: the picture is a map over an array.
//
// ── IT SAYS WHAT IT CANNOT DO ────────────────────────────────────────
// A pairing can only become a live duel through the starter seam
// (setPairingStarter), and nothing has registered one yet — that needs
// caP'n's matchmaking lap with two real accounts. So a ready pairing
// shows "AWAITING THE PILOT" and a REPORT control, rather than a PLAY
// button that would do nothing. Reporting by hand is not a placeholder
// for the duel; it is how a bracket stays usable while the duel path is
// still being flown, and it is exactly what a paper bracket does.
//
// ── 2026-08-22 — caP'n LIFTS THE DECREE ───────────────────────────────
// Two axes got added here, mirrored from tournament.js:
//   BRACKET STYLE — Classic 1v1 (unchanged, still the default) or the
//     new FFA Gauntlet (4 entrants = one 4-seat FFA; 8 = two 4-seat FFA
//     pools feeding a 1v1 final). Every place below that used to assume
//     a match has exactly two named slots (`m.a`/`m.b`) now reads
//     `m.seats`, an array, so the bracket picture and the standings
//     table work for a 4-wide pool the same pass they work for a pair.
//   TOURNAMENT KIND — Free (no fee, "cheater decks" i.e. deckMode
//     "open") or Tournament of Kings (an entrance fee in ◕ Chocolate
//     Coins, canon decks, and a 60/30/10 payout on a champion). The
//     payout preview here is arithmetic only — splitPot() is the same
//     function tournament.js's finalizeTournament actually pays with,
//     so what a coordinator previews IS what gets paid, not a separate
//     guess at it.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState, useRef } from "react";
import { GM } from "./theme";
import {
  STATUS, BRACKET_STYLES, SIZES_BY_STYLE, normalizeBracketStyle,
  KIND, splitPot, deriveBracket, entrantList, liveMatches, myMatch,
  standings, roundName, createTournament, joinTournament, lockBracket,
  reportResult, finalizeTournament, listenToTournament, listOpenTournaments,
  canStartPairings, onPairingReady,
} from "./tournament";

const btn = (color, on = true) => ({
  all: "unset", cursor: on ? "pointer" : "not-allowed",
  fontFamily: "'Inter', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.08em",
  padding: "5px 10px", borderRadius: 4, whiteSpace: "nowrap",
  border: `1px solid ${color}66`, color, opacity: on ? 1 : 0.4,
});

// A "pill" toggle — same face as `btn` but with a filled/selected state,
// for the two either/or choices (bracket style, tournament kind) that
// weren't a thing before this pass. GM.page is the void-glass floor
// color, dark enough to read as text ON a gold fill.
const pill = (color, active) => ({
  all: "unset", cursor: "pointer",
  fontFamily: "'Inter', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.08em",
  padding: "5px 10px", borderRadius: 4, whiteSpace: "nowrap",
  border: `1px solid ${active ? color : `${color}66`}`,
  color: active ? GM.page : color,
  background: active ? color : "transparent",
});

const numInput = {
  width: 64, background: GM.pageMid, border: `1px solid ${GM.pageBorder}`,
  color: GM.vellum, borderRadius: 4, padding: "4px 6px", fontSize: 11,
  fontFamily: "'Inter', system-ui, sans-serif",
};
const selectStyle = { ...numInput, width: 118 };

const styleLabel = (s) => (normalizeBracketStyle(s) === BRACKET_STYLES.GAUNTLET ? "FFA Gauntlet" : "Classic 1v1");
const kindLabel = (k) => (k === KIND.KINGS ? "Tournament of Kings" : "Free Tournament");

export default function TournamentBoard({ uid, name, profile, onEnterGame }) {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createErr, setCreateErr] = useState(null);

  // The two new either/or choices. Sizes on offer are DERIVED from
  // `style` (SIZES_BY_STYLE) rather than hardcoded, so BRACKET_SIZES
  // growing or GAUNTLET_SIZES changing never needs a second edit here.
  const [style, setStyle] = useState(BRACKET_STYLES.CLASSIC);
  const [kind, setKind] = useState(KIND.FREE);
  const [feeText, setFeeText] = useState("100");

  const refresh = React.useCallback(() => {
    listOpenTournaments().then(setList).catch(() => setList([]));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (openId) return <OneTournament id={openId} uid={uid} name={name} profile={profile} onEnterGame={onEnterGame} onBack={() => { setOpenId(null); refresh(); }} />;

  const sizes = SIZES_BY_STYLE[style];
  const feeAmount = Math.floor(Number(feeText));
  const feeValid = feeAmount > 0;

  // What splitPot() will actually pay, previewed against a FULL table at
  // this size — the only pot number that's known before a coordinator
  // has entrants. hasThird follows the exact same rule
  // deriveGauntletBracket/deriveClassicBracket use: only an 8-entrant
  // Gauntlet ever produces a real 3rd.
  const payoutPreview = (size) => {
    const hasThird = style === BRACKET_STYLES.GAUNTLET && size === 8;
    const pot = size * feeAmount;
    const split = splitPot(pot, hasThird);
    return hasThird
      ? `pot ◕${pot} — 1st ◕${split.first} · 2nd ◕${split.second} · 3rd ◕${split.third}`
      : `pot ◕${pot} — 1st ◕${split.first} · 2nd ◕${split.second}`;
  };

  return (
    <div>
      <div style={{ fontSize: 11, color: GM.vellumFaint, fontStyle: "italic", lineHeight: 1.7, marginBottom: 12 }}>
        A bracket of Solrends. Seeds 1 and 2 can only meet in the final — the
        bracket is built so that is true, not hoped.
      </div>

      {/* BRACKET STYLE */}
      <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 5 }}>BRACKET STYLE</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <button style={pill(GM.inkGoldBright, style === BRACKET_STYLES.CLASSIC)} onClick={() => setStyle(BRACKET_STYLES.CLASSIC)}>
          CLASSIC 1v1
        </button>
        <button style={pill(GM.inkGoldBright, style === BRACKET_STYLES.GAUNTLET)} onClick={() => setStyle(BRACKET_STYLES.GAUNTLET)}>
          FFA GAUNTLET
        </button>
      </div>
      {style === BRACKET_STYLES.GAUNTLET && (
        <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", marginBottom: 10, lineHeight: 1.6 }}>
          4 entrants: one 4-way free-for-all, winner takes it. 8 entrants: two
          4-way pools run in parallel, then the two pool winners meet 1v1 in
          the final. No byes — the table has to fill exactly before it can lock.
        </div>
      )}

      {/* TOURNAMENT KIND */}
      <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 5 }}>TOURNAMENT KIND</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button style={pill(GM.inkGold, kind === KIND.FREE)} onClick={() => setKind(KIND.FREE)}>FREE TOURNAMENT</button>
        <button style={pill(GM.inkGold, kind === KIND.KINGS)} onClick={() => setKind(KIND.KINGS)}>TOURNAMENT OF KINGS</button>
        {kind === KIND.KINGS && (
          <span style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: 4 }}>
            <span style={{ fontSize: 10, color: GM.vellumDim }}>entry fee ◕</span>
            <input type="number" min="1" step="1" value={feeText}
              onChange={(e) => setFeeText(e.target.value)} style={numInput} />
          </span>
        )}
      </div>
      <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", marginBottom: 10, lineHeight: 1.6 }}>
        {kind === KIND.KINGS
          ? (feeValid
              ? "Canon decks only. Winnings split 60% / 30% / 10% out of the collected entry fees."
              : "Canon decks only — set a whole-number entry fee above 0 before opening.")
          : "Open decks. No entry fee, no payout — pure bragging rights."}
      </div>

      {/* SIZES — filtered to what this style actually offers */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        {sizes.map((size) => {
          const disabled = busy || (kind === KIND.KINGS && !feeValid);
          return (
            <div key={size} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <button disabled={disabled} style={btn(GM.inkGoldBright, !disabled)}
                onClick={async () => {
                  if (!uid || disabled) return;
                  setBusy(true); setCreateErr(null);
                  const res = await createTournament(uid, {
                    name: `${name || "An Aenarchist"}'s ${size}`,
                    size, bracketStyle: style, kind,
                    entranceFee: kind === KIND.KINGS ? { amount: feeAmount } : undefined,
                  });
                  if (res.ok) { await joinTournament(res.id, uid, name); setOpenId(res.id); }
                  else setCreateErr(res.reason);
                  setBusy(false); refresh();
                }}>
                + OPEN A {size}
              </button>
              {kind === KIND.KINGS && feeValid && (
                <span style={{ fontSize: 8, color: GM.vellumFaint, textAlign: "center", maxWidth: 120 }}>
                  {payoutPreview(size)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {createErr && (
        <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", marginBottom: 10 }}>
          Couldn't open that one ({createErr}).
        </div>
      )}

      {list === null && <div style={{ color: GM.vellumFaint, fontSize: 11, fontStyle: "italic", padding: "16px 0", textAlign: "center" }}>◆ Reading the board…</div>}
      {list && !list.length && (
        <div style={{ textAlign: "center", padding: "22px 8px", color: GM.vellumFaint, fontSize: 11, fontStyle: "italic", lineHeight: 1.7 }}>
          ◇ No tournaments standing.<br />Open one and the bracket waits for entrants.
        </div>
      )}

      <div style={{ display: "grid", gap: 7 }}>
        {(list || []).map((t) => (
          <div key={t.id} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "9px 11px",
            borderRadius: 6, border: `1px solid ${GM.pageBorder}`, background: GM.pageMid,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: GM.vellum, fontFamily: GM.font, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</div>
              <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 2 }}>
                {styleLabel(t.bracketStyle)} · {kindLabel(t.kind)} · {t.entrants}/{t.size} entrants · {t.status === STATUS.OPEN ? "taking entrants" : "running"}
              </div>
            </div>
            <button style={btn(GM.inkGoldBright)} onClick={() => setOpenId(t.id)}>OPEN</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// A pairing's report control. Exactly two seats (a classic pairing, or a
// Gauntlet final) keeps the original one-tap "X WON" buttons — nothing
// about that flow changed. A wider FFA pool needs a winner AND, ideally,
// a runner-up: deriveGauntletBracket's 3rd-place convention reads the
// runner-up straight off THIS report for round-0 matches, so hand-
// reporting an FFA result is where that data actually gets born while
// the duel-start seam is still unwired.
function MatchReporter({ id, m, busy, setBusy }) {
  const seats = (m.seats || []).filter(Boolean);
  const [winner, setWinner] = useState("");
  const [runnerUp, setRunnerUp] = useState("");

  if (seats.length <= 2) {
    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {seats.map((s) => (
          <button key={s.uid} style={btn(GM.inkGoldBright, !busy)} onClick={async () => {
            setBusy(m.key); await reportResult(id, m.key, s.uid); setBusy(null);
          }}>{s.name} WON</button>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <select value={winner} style={selectStyle} onChange={(e) => {
        setWinner(e.target.value);
        if (e.target.value === runnerUp) setRunnerUp("");
      }}>
        <option value="">Winner…</option>
        {seats.map((s) => <option key={s.uid} value={s.uid}>{s.name}</option>)}
      </select>
      <select value={runnerUp} style={selectStyle} onChange={(e) => setRunnerUp(e.target.value)}>
        <option value="">2nd (optional)…</option>
        {seats.filter((s) => s.uid !== winner).map((s) => <option key={s.uid} value={s.uid}>{s.name}</option>)}
      </select>
      <button disabled={busy || !winner} style={btn(GM.inkGoldBright, !busy && !!winner)} onClick={async () => {
        setBusy(m.key); await reportResult(id, m.key, winner, null, runnerUp || null); setBusy(null);
      }}>REPORT</button>
    </div>
  );
}

function OneTournament({ id, uid, name, profile, onEnterGame, onBack }) {
  const [data, setData] = useState(undefined);
  const [busy, setBusy] = useState(null);
  // H30 — once a starter is registered and PLAY has produced a code for
  // MY pairing, this is where I land: `null` until I'm actually seated,
  // then my own seat index in that match, so "ENTER SHATTERFIELD" below
  // knows who I am there.
  const [mySeat, setMySeat] = useState(null);
  const [seatingCode, setSeatingCode] = useState(null);
  const [seatingBusy, setSeatingBusy] = useState(false);

  useEffect(() => listenToTournament(id, setData), [id]);

  const style = normalizeBracketStyle(data?.meta?.bracketStyle);
  const entrants = useMemo(() => entrantList(data?.entrants), [data]);
  const bracket = useMemo(() => deriveBracket(entrants, data?.results || {}, style), [entrants, data, style]);
  const mine = myMatch(bracket, uid);
  // A new round means a new pairing key — the seat/code from the LAST
  // one is meaningless here (it would point "ENTER SHATTERFIELD" at a
  // finished match), so seating state resets the moment the pairing
  // identity changes.
  const mineKeyRef = useRef(mine?.key);
  if (mineKeyRef.current !== mine?.key) {
    mineKeyRef.current = mine?.key;
    if (mySeat != null || seatingCode != null) { setMySeat(null); setSeatingCode(null); }
  }
  const ready = liveMatches(bracket);
  const table = standings(bracket);
  const isHost = data?.meta?.createdBy === uid;
  const inIt = !!(data?.entrants || {})[uid];
  const open = data?.meta?.status === STATUS.OPEN;
  const isGauntlet = style === BRACKET_STYLES.GAUNTLET;
  const canLock = isGauntlet ? entrants.length === data?.meta?.size : entrants.length >= 2;

  // A Kings tournament pays out the moment a champion exists. Any client
  // watching can fire this — finalizeTournament's own meta.status
  // transaction is what makes it safe for more than one of them to try
  // at once; only the caller that actually flips RUNNING→DONE computes
  // and credits the split, everyone else's call is a harmless no-op.
  useEffect(() => {
    if (bracket.champion && data?.meta?.kind === KIND.KINGS && data?.meta?.status !== STATUS.DONE) {
      finalizeTournament(id, bracket, entrants.length).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, bracket.champion, data?.meta?.kind, data?.meta?.status, entrants.length]);

  // ── H30 · SEATING MYSELF ─────────────────────────────────────
  // A registered starter (setPairingStarter, see Plaza.js) turns MY
  // pairing into a real match the instant someone in it clicks PLAY —
  // but createMatch only seats WHOEVER CALLED IT. Everyone else in the
  // pairing (which, for a Gauntlet round-0 pool, is up to three other
  // people) needs to notice the resulting code and claim their own
  // seat — the exact same "share the code, the other side joins"
  // rendezvous every human match in this game already uses, just
  // triggered by watching this pairing's result instead of a kiosk
  // code screen.
  //
  // Runs once per code: reads the match, and if I'm not already one
  // of its seats (I might be — I could be the one who clicked PLAY),
  // joins with my own resolved deck. Safe to fire on every render of a
  // new code because `seatingBusy` and "already seated" both short-
  // circuit it, so a remount or a duplicate effect run never double-
  // joins.
  const pairingCode = mine && data?.results?.[mine.key]?.code;
  useEffect(() => {
    if (!pairingCode || !uid || seatingBusy || mySeat != null) return;
    let cancelled = false;
    setSeatingBusy(true);
    (async () => {
      try {
        const { getMatch, joinMatch, drawAndDepleteCards } = await import("./gameManager");
        const m = await getMatch(pairingCode);
        if (!m || cancelled) return;
        const seatCount = m.seatCount || 2;
        let foundSeat = null;
        for (let i = 0; i < seatCount; i++) {
          if (m.players?.[i]?.uid === uid) { foundSeat = i; break; }
        }
        if (foundSeat == null) {
          const { resolveDeck } = await import("./Plaza"); // dynamic — Plaza.js imports THIS file statically, so a top-level import back would cycle
          const deck = resolveDeck(profile);
          if (!deck || cancelled) return;
          const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deck.grimoire || {}, [], 5);
          const joined = await joinMatch(
            pairingCode, name, depletedGrimoire, deck.luxmoire || {}, deck.prophecy || null,
            startingHand, deck.curios || [null, null], deck.esclings || null
          );
          if (cancelled) return;
          if (joined?.error) return; // match filled/vanished between the read above and the join — MatchReporter is still there as a fallback
          foundSeat = Number.isInteger(joined?.seatIdx) ? joined.seatIdx : null;
        }
        if (!cancelled && foundSeat != null) { setMySeat(foundSeat); setSeatingCode(pairingCode); }
      } catch (e) {
        console.warn("[tournament] auto-seat failed:", e?.message);
      } finally {
        if (!cancelled) setSeatingBusy(false);
      }
    })();
    // AUDIT FIX (2026-08-22): cancelling an in-flight attempt used to
    // leave `seatingBusy` stuck true forever — its only reset lived in
    // the async `finally`, gated by `!cancelled`, so a `pairingCode`
    // change mid-join skipped it and the guard above then blocked every
    // future auto-seat attempt for this component instance. Reset it
    // here too: abandoning this attempt should free the next one to run.
    return () => { cancelled = true; setSeatingBusy(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairingCode, uid]);

  if (data === undefined) return <div style={{ color: GM.vellumFaint, fontSize: 11, fontStyle: "italic", padding: "18px 0", textAlign: "center" }}>◆ Reading the bracket…</div>;
  if (data === null) return <div style={{ color: GM.vellumDim, fontSize: 11, fontStyle: "italic" }}>That tournament is gone. <button style={btn(GM.inkGoldBright)} onClick={onBack}>← Back</button></div>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <button aria-label="Back" style={btn(GM.vellumDim)} onClick={onBack}>←</button>
        <span style={{ fontFamily: GM.font, fontSize: 14, color: GM.vellum }}>{data.meta?.name}</span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: GM.vellumFaint }}>
          {styleLabel(data.meta?.bracketStyle)} · {kindLabel(data.meta?.kind)} · {entrants.length}/{data.meta?.size} · {data.meta?.status}
        </span>
      </div>

      {bracket.champion && (
        <div style={{
          marginBottom: 6, padding: "8px 12px", borderRadius: 6,
          border: `1px solid ${GM.inkGoldBright}`, background: `${GM.inkGoldBright}12`,
          color: GM.inkGoldBright, fontSize: 12, fontStyle: "italic", textAlign: "center",
        }}>
          ◆ {bracket.champion.name} takes it.
        </div>
      )}
      {bracket.runnerUp && (
        <div style={{ marginBottom: 6, fontSize: 10.5, color: GM.vellumDim, textAlign: "center" }}>
          ◇ {bracket.runnerUp.name} — runner-up.
        </div>
      )}
      {bracket.third && (
        <div style={{ marginBottom: 12, fontSize: 10, color: GM.vellumFaint, textAlign: "center" }}>
          · {bracket.third.name} — third place.
        </div>
      )}

      {/* LOBBY CONTROLS */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        {open && !inIt && (
          <button style={btn(GM.inkGoldBright, !busy)} onClick={async () => {
            setBusy("join"); await joinTournament(id, uid, name); setBusy(null);
          }}>{busy === "join" ? "…" : "ENTER"}</button>
        )}
        {open && isHost && canLock && (
          <button style={btn(GM.inkGoldBright, !busy)} onClick={async () => {
            setBusy("lock"); await lockBracket(id); setBusy(null);
          }}>{busy === "lock" ? "…" : "LOCK THE BRACKET"}</button>
        )}
        {open && !canLock && (
          <span style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic" }}>
            {isGauntlet
              ? `An FFA table needs exactly ${data?.meta?.size} entrants to lock — no byes here.`
              : "Two entrants and the bracket can lock."}
          </span>
        )}
      </div>

      {/* YOUR MATCH */}
      {mine && (
        <div style={{
          marginBottom: 12, padding: "9px 12px", borderRadius: 6,
          border: `1px solid ${GM.inkGold}`, background: GM.pageMid,
        }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: GM.inkGoldBright, marginBottom: 5 }}>YOUR MATCH</div>
          <div style={{ fontSize: 12, color: GM.vellum, marginBottom: 7 }}>
            {(mine.seats || []).filter(Boolean).map((s) => s.name).join(" vs ")} — {roundName(mine.round, bracket.rounds.length, style)}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 7 }}>
            {mySeat != null && seatingCode
              ? <button style={btn(GM.inkGoldBright, true)} onClick={() => onEnterGame && onEnterGame(seatingCode, mySeat, name)}>
                  ◆ ENTER SHATTERFIELD
                </button>
              : pairingCode
                ? <span style={{ fontSize: 9.5, color: GM.vellumFaint, fontStyle: "italic" }}>
                    ◆ Seating you at the table…
                  </span>
                : canStartPairings()
                  ? <button style={btn(GM.inkGoldBright, !busy)} onClick={async () => {
                      setBusy(mine.key); await onPairingReady(id, mine, data.meta); setBusy(null);
                    }}>PLAY</button>
                  : <span style={{ fontSize: 9.5, color: GM.vellumFaint, fontStyle: "italic" }}>
                      AWAITING THE PILOT — no duel starter is registered yet, so report the result by hand.
                    </span>}
          </div>
          <MatchReporter key={mine.key} id={id} m={mine} busy={busy} setBusy={setBusy} />
        </div>
      )}

      {/* THE BRACKET — one map over an array, no recursion. `m.seats` is
          however many slots a match actually has (2 for classic/final,
          4 for a Gauntlet pool) — nothing here assumes exactly two. */}
      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8 }}>
        {bracket.rounds.map((round, ri) => (
          <div key={ri} style={{ minWidth: 148, flex: "0 0 auto" }}>
            <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 6 }}>
              {roundName(ri, bracket.rounds.length, style).toUpperCase()}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {round.map((m) => (
                <div key={m.key} style={{
                  border: `1px solid ${m.live ? GM.inkGold : GM.pageBorder}`,
                  borderRadius: 5, overflow: "hidden", background: GM.page,
                  opacity: m.walkover ? 0.5 : 1,
                }}>
                  {(m.seats || []).map((p, si) => {
                    const won = m.winner && p && m.winner.uid === p.uid;
                    const isRunnerUp = !won && m.runnerUp && p && m.runnerUp.uid === p.uid;
                    return (
                      <div key={p ? p.uid : `empty${si}`} style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "4px 7px", fontSize: 10.5, fontFamily: GM.font,
                        color: won ? GM.inkGoldBright : p ? GM.vellumDim : GM.vellumFaint,
                        background: won ? `${GM.inkGoldBright}10` : "transparent",
                        borderBottom: si < m.seats.length - 1 ? `1px solid ${GM.pageBorder}` : "none",
                      }}>
                        <span style={{ fontSize: 8, width: 14, color: GM.vellumFaint }}>{p ? p.seed : ""}</span>
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p ? p.name : m.walkover ? "—" : "…"}
                        </span>
                        {won && <span style={{ fontSize: 9 }}>◆</span>}
                        {isRunnerUp && <span style={{ fontSize: 8, color: GM.vellumFaint }}>2nd</span>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* STANDINGS — reads as a story, not a list of losers */}
      {table.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 6 }}>HOW IT WENT</div>
          <div style={{ display: "grid", gap: 3 }}>
            {table.map((p) => (
              <div key={p.uid} style={{ fontSize: 10.5, color: p.champion ? GM.inkGoldBright : GM.vellumDim, fontFamily: GM.font }}>
                {p.champion ? "◆ " : "· "}{p.name}
                <span style={{ color: GM.vellumFaint, marginLeft: 6, fontSize: 9.5 }}>
                  {p.champion ? "took it" : `out in ${roundName(p.outIn, bracket.rounds.length, style).toLowerCase()}${p.runnerUp ? " (runner-up)" : ""}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* THE PAYOUT — once finalizeTournament has actually credited it.
          Names come from `entrants` (the frozen roster this bracket was
          built from); a uid finalizeTournament wrote that isn't in it
          any more just prints bare, which should never happen since the
          roster is frozen at lockBracket. */}
      {data?.payout && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 6 }}>THE PAYOUT</div>
          <div style={{ display: "grid", gap: 3 }}>
            {Object.entries(data.payout).filter(([, amt]) => amt > 0).map(([payeeUid, amt]) => {
              const who = entrants.find((e) => e.uid === payeeUid);
              return (
                <div key={payeeUid} style={{ fontSize: 10.5, color: GM.vellumDim, fontFamily: GM.font }}>
                  ◕ {amt} — {who ? who.name : payeeUid}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!ready.length && !bracket.champion && !open && (
        <div style={{ marginTop: 10, fontSize: 10, color: GM.vellumFaint, fontStyle: "italic" }}>
          Nothing to play right now — the bracket is waiting on a result somewhere above.
        </div>
      )}
    </div>
  );
}
